import * as React from 'react';

import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import httpLaunchRscript from 'consumers/httpLaunchRscript';
import {
  buildJobInput,
  clampValues,
  cliCommand,
  createRunNonce,
  invalidArgs,
} from 'services/jobSpecs';
import type { JobInput, JobSpec, JobValues } from 'services/jobSpecs';

import JobFields from './JobFields';
import JobPreview from './JobPreview';

/**
 * Generic launcher for a declared job.
 *
 * Owns the form values, the computed payload and the submission. It knows
 * nothing about any particular job — a spec supplies the fields, the flags and
 * the consequence to confirm.
 *
 * The nonce lives here rather than in the spec so the version id shown in the
 * preview is the one that gets submitted. It is reissued whenever the inputs
 * change — a different run should not keep the previous attempt's id — and
 * again after a successful launch so a second run of the same pull date does
 * not collide with the first.
 */

type Props = {
  spec: JobSpec;
  initialValues: JobValues;
  environment: SensoryLinkEnvironment | null;
  token: string;
  /** conchBatch rejects anyone else, so a button would only ever 403 */
  allowed: boolean;
  onSubmitted?: (result: { batchJobId: string; input: JobInput }) => void;
};

const JobLauncher: React.FC<Props> = ({
  spec,
  initialValues,
  environment,
  token,
  allowed,
  onSubmitted,
}) => {
  const [values, setValues] = React.useState<JobValues>(() =>
    clampValues(spec, initialValues),
  );
  const [nonce, setNonce] = React.useState(createRunNonce);
  const [confirming, setConfirming] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const input = React.useMemo(
    () => buildJobInput(spec, values, environment, nonce),
    [spec, values, environment, nonce],
  );

  const basic = spec.fields.filter((f) => !f.advanced);
  const advanced = spec.fields.filter((f) => f.advanced);

  // Mirrors conchBatch's own arg pattern, so a value it would 400 on is caught
  // here with the offending flag named rather than after a round trip.
  const rejected = invalidArgs(input.args);
  const blocked = !environment || rejected.length > 0;

  const onChange = (name: string, value: string | number) => {
    setValues((current) => clampValues(spec, { ...current, [name]: value }));
    // A new attempt, a new id — even when the date in the version string
    // would not have moved, the hash should not still name the previous cut.
    setNonce(createRunNonce());
    setError(null);
  };

  const onSubmit = async () => {
    // The dialog stays mounted and clickable through its ~195ms exit
    // transition, so a double-click would re-enter here with the same version
    // id and start a second training run against one job row.
    if (submitting) return;
    setConfirming(false);
    setSubmitting(true);
    setError(null);
    try {
      const result = await httpLaunchRscript(
        input.script_path,
        input.args,
        token,
        { jobDefinition: input.job_definition, jobQueue: input.job_queue },
      );
      onSubmitted?.({ batchJobId: result.job_id, input });
      // A fresh id for the next run: the same pull date is a legitimate
      // re-run, and reusing the version would land two runs on one row.
      setNonce(createRunNonce());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {spec.description}
      </Typography>

      <Box sx={{ maxWidth: 420 }}>
        <JobFields
          fields={basic}
          values={values}
          onChange={onChange}
          disabled={submitting}
        />
      </Box>

      {advanced.length > 0 && (
        <Accordion
          disableGutters
          elevation={0}
          // unmountOnExit: a collapsed accordion otherwise keeps its inputs in
          // the DOM and in the tab order, so the "simple" form is only simple
          // to look at. The values live above these fields, so nothing is lost.
          TransitionProps={{ unmountOnExit: true }}
          sx={{ mt: 1, maxWidth: 420, '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
            <Typography variant="body2">Advanced</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <JobFields
              fields={advanced}
              values={values}
              onChange={onChange}
              disabled={submitting}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ mt: 2 }}>
        <JobPreview input={input} />
      </Box>

      {!environment && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Still working out which environment this console is running against.
          Nothing can be launched until it does — an unknown environment would
          default to the production job definition.
        </Alert>
      )}

      {rejected.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          conchBatch will not accept {rejected.join(', ')}. Arguments must look
          like <code>--key=value</code> with no spaces.
        </Alert>
      )}

      {!allowed && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Starting this needs superadmin. Ask someone who has it, or run it
          directly:
          <Box
            component="pre"
            sx={{
              mt: 1,
              mb: 0,
              px: 1,
              py: 0.5,
              backgroundColor: 'rgba(0,0,0,0.06)',
              borderRadius: 1,
              fontSize: '0.78rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            {cliCommand(input)}
          </Box>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {allowed && (
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          disabled={submitting || blocked}
          onClick={() => (spec.confirm ? setConfirming(true) : onSubmit())}
        >
          {submitting ? 'Starting…' : 'Go'}
        </Button>
      )}

      {spec.confirm && (
        <Dialog
          open={confirming}
          onClose={() => setConfirming(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{spec.confirm.title}</DialogTitle>
          <DialogContent>
            {/* Env first, then the flags in the order the spec builds them, and
                the hardware last. Derived from the payload rather than listed
                by hand, so a spec that grows a flag shows it here too. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 2,
                rowGap: 0.5,
                fontSize: '0.85rem',
              }}
            >
              {[
                ['Env', input.job_definition ?? 'unknown'],
                ...input.args.map((arg) => [
                  arg.slice(2, arg.indexOf('=')),
                  arg.slice(arg.indexOf('=') + 1),
                ]),
                ['Hardware', input.job_queue],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <Box sx={{ color: 'text.secondary' }}>{label}</Box>
                  <Box component="code" sx={{ wordBreak: 'break-all' }}>
                    {value}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirming(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={submitting}
            >
              Start run
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default JobLauncher;
